import { Card, Col, Table, Button } from 'react-bootstrap'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { UpdateOrgSubscriptionIdModal } from 'components/organization-details-page/update-org-subscription-id-modal'
import { OverrideSubscriptionStatusModal } from 'components/organization-details-page/override-subscription-status-modal'
import useOrganizationSubscriptionItems from 'hooks/use-organization-subscription-items'

const UserOrganizationCard = ({
  org,
  accountManagerName,
  salesRepName,
  userName,
}: {
  org: any
  accountManagerName?: string
  salesRepName?: string
  userName?: string
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showSubscriptionIdUpdateModal, setShowSubscriptionIdUpdateModal] =
    useState(false)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const { data: subscriptionItems } = useOrganizationSubscriptionItems(org?.id)
  const orgSub = org?.organization_subscription
  const isInHouseTrial =
    orgSub?.subscription_status === 'trialing' &&
    orgSub?.trial_end &&
    orgSub?.subscription_id?.includes('trial')

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 3000)
  }

  const CopyableField = ({ text, field }: { text: string; field: string }) => (
    <div
      onClick={() => handleCopy(text, field)}
      style={{ cursor: 'pointer', display: 'inline-block' }}
      title={copiedField === field ? 'Copied!' : 'Click to copy'}
    >
      {text}{' '}
      <ion-icon
        name={copiedField === field ? 'checkmark-outline' : 'copy-outline'}
        style={{
          color: copiedField === field ? '#28a745' : '#10B981',
          fontSize: '16px',
        }}
      />
    </div>
  )

  const handleEditSubscription = () => {
    setShowSubscriptionIdUpdateModal(true)
  }

  const handleOverrideSubscription = () => {
    setShowOverrideModal(true)
  }

  return (
    <Col md="12">
      <Card className="mt-4">
        <Card.Header>
          <h4 className="mb-0">Org Information</h4>
        </Card.Header>
        <Card.Body>
          {org ? (
            <>
              {/* Org ID */}
              <div className="mb-3">
                <div className="fw-bold  mb-1">Org ID</div>
                <div>
                  <CopyableField text={org.id.toString()} field="orgId" />
                </div>
              </div>

              {/* Created on */}
              <div className="mb-3">
                <div className="fw-bold  mb-1">Created on</div>
                <div>
                  {moment(org.created_at).format('MMMM DD, YYYY h:mm A')}
                </div>
              </div>

              {/* Org Name */}
              <div className="mb-3">
                <div className="fw-bold  mb-1">Org Name</div>
                <div className="d-flex align-items-center justify-content-between">
                  <Link
                    to={`/organizations/${org.id}`}
                    style={{
                      color: '#10B981',
                      textDecoration: 'underline',
                    }}
                  >
                    {org.name}
                  </Link>
                  <Link
                    to={`/organizations/${org.id}`}
                    style={{
                      color: '#10B981',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <ion-icon
                      name="open-outline"
                      style={{
                        fontSize: '16px',
                        cursor: 'pointer',
                      }}
                    />
                  </Link>
                </div>
              </div>

              {/* Account Manager */}
              <div className="mb-3">
                <div className="fw-bold  mb-1">Account Manager</div>
                <div>{accountManagerName || 'N/A'}</div>
              </div>

              {/* Sales Rep */}
              <div className="mb-3">
                <div className="fw-bold  mb-1">Sales Rep</div>
                <div>{salesRepName || 'N/A'}</div>
              </div>

              {/* Hubspot URL */}
              {org.internal_crm_url && (
                <div className="mb-3">
                  <div className="fw-bold  mb-1">Hubspot URL</div>
                  <div>
                    <a
                      href={org.internal_crm_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#10B981', textDecoration: 'underline' }}
                    >
                      {org.internal_crm_url}
                    </a>
                  </div>
                </div>
              )}

              {/* Stripe Subscription */}
              {orgSub && (
                <div className="mb-3">
                  <div className="fw-bold mb-1">Stripe Subscription</div>
                  <div className="mb-2">
                    {!isInHouseTrial ? (
                      <a
                        href={`https://dashboard.stripe.com/subscriptions/${orgSub.subscription_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#10B981',
                          textDecoration: 'underline',
                        }}
                      >
                        {orgSub.subscription_id}
                      </a>
                    ) : (
                      orgSub.subscription_id
                    )}
                  </div>
                  <div className="d-flex gap-3">
                    <span
                      style={{
                        color: '#10B981',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                      }}
                      onClick={handleEditSubscription}
                      onMouseEnter={(e: any) => {
                        e.target.style.textDecoration = 'underline'
                      }}
                      onMouseLeave={(e: any) => {
                        e.target.style.textDecoration = 'none'
                      }}
                    >
                      Edit
                    </span>
                    <span
                      style={{
                        color: '#10B981',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                      }}
                      onClick={handleOverrideSubscription}
                      onMouseEnter={(e: any) => {
                        e.target.style.textDecoration = 'underline'
                      }}
                      onMouseLeave={(e: any) => {
                        e.target.style.textDecoration = 'none'
                      }}
                    >
                      Override
                    </span>
                  </div>
                </div>
              )}

              {/* Product Table */}
              {subscriptionItems && subscriptionItems.length > 0 && (
                <div className="mt-4">
                  <Table
                    responsive
                    variant="dark"
                    style={{
                      backgroundColor: '#444444',
                      border: '1px solid #888888',
                      borderRadius: '10px',
                      overflow: 'hidden',
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#333' }}>
                        <th
                          style={{
                            border: 'none',
                            padding: 'clamp(8px, 2vw, 16px)',
                            fontWeight: '500',
                            color: 'white',
                            fontSize: 'clamp(12px, 2.5vw, 14px)',
                          }}
                        >
                          Product
                        </th>
                        <th
                          style={{
                            border: 'none',
                            padding: 'clamp(8px, 2vw, 16px)',
                            fontWeight: '500',
                            color: 'white',
                            fontSize: 'clamp(12px, 2.5vw, 14px)',
                          }}
                        >
                          Status
                        </th>
                        <th
                          style={{
                            border: 'none',
                            padding: 'clamp(8px, 2vw, 16px)',
                            fontWeight: '500',
                            color: 'white',
                            fontSize: 'clamp(12px, 2.5vw, 14px)',
                            textAlign: 'center',
                          }}
                        >
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptionItems.map((item, index) => (
                        <tr
                          key={item.product_name}
                          style={{
                            borderTop: index === 0 ? 'none' : '1px solid #555',
                          }}
                        >
                          <td
                            style={{
                              border: 'none',
                              padding: 'clamp(8px, 2vw, 16px)',
                              color: 'white',
                              fontSize: 'clamp(11px, 2.2vw, 13px)',
                              wordBreak: 'break-word',
                            }}
                          >
                            {item.product_name}
                          </td>
                          <td
                            style={{
                              border: 'none',
                              padding: 'clamp(8px, 2vw, 16px)',
                              color: 'white',
                              fontSize: 'clamp(11px, 2.2vw, 13px)',
                            }}
                          >
                            {item.status}
                          </td>
                          <td
                            style={{
                              border: 'none',
                              padding: 'clamp(8px, 2vw, 16px)',
                              color: 'white',
                              fontSize: 'clamp(11px, 2.2vw, 13px)',
                              textAlign: 'center',
                            }}
                          >
                            {item.quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Commented out sections */}
              {/* {org.internal_crm_url && (
                <div style={{ marginBottom: '12px' }}>
                  <HubSpotButton url={org.internal_crm_url} />
                </div>
              )} */}

              {/* <TextInformationLine
                label="Org ID"
                data={<CopyableField text={org.id.toString()} field="orgId" />}
              />
              <TextInformationLine
                label="Org Name"
                data={
                  <Link
                    to={`/organizations/${org.id}`}
                    style={{ color: '#4da6ff', textDecoration: 'underline' }}
                  >
                    {org.name}
                  </Link>
                }
              />
              <TextInformationLine
                label="Account Manager"
                data={accountManagerName || 'N/A'}
              />
              <TextInformationLine
                label="Sales Rep"
                data={salesRepName || 'N/A'}
              />
              <TextInformationLine
                label="Created At"
                data={moment(org.created_at).format('LLL')}
              /> */}
            </>
          ) : (
            <p>{userName || 'User'} is not associated with any organization</p>
          )}
        </Card.Body>
      </Card>

      {org && (
        <>
          <UpdateOrgSubscriptionIdModal
            show={showSubscriptionIdUpdateModal}
            handleClose={() => setShowSubscriptionIdUpdateModal(false)}
            org={org}
            setShowSubscriptionIdUpdateModal={setShowSubscriptionIdUpdateModal}
          />
          <OverrideSubscriptionStatusModal
            show={showOverrideModal}
            handleClose={() => setShowOverrideModal(false)}
            orgId={org.id.toString()}
            organizationSubscription={org.organization_subscription}
          />
        </>
      )}
    </Col>
  )
}

export default UserOrganizationCard
